import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Download,
} from 'lucide-react';
import { useEmailConfigSlice } from '../../slice';

interface EmailStatsData {
  totalSent: number;
  delivered: number;
  failed: number;
  bounced: number;
  opened: number;
  clicked: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  timeSeriesData: Array<{
    date: string;
    sent: number;
    delivered: number;
    failed: number;
  }>;
  providerStats: Array<{
    provider: string;
    sent: number;
    delivered: number;
    failed: number;
    deliveryRate: number;
  }>;
}

const EmailStats: React.FC = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [statsData, setStatsData] = useState<EmailStatsData | null>(null);
  const [loading, setLoading] = useState(true);

  const { useGetEmailStatsQuery } = useEmailConfigSlice();
  const [getEmailStats, { data: statsResponse }] = useGetEmailStatsQuery();

  useEffect(() => {
    getEmailStats({ timeRange });
  }, [timeRange]);

  useEffect(() => {
    if (statsResponse?.data) {
      setStatsData(statsResponse.data);
    }
    setLoading(false);
  }, [statsResponse]);

  const handleExport = () => {
    // Export functionality
    console.log('Exporting stats...');
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading email statistics...</p>
        </div>
      </div>
    );
  }

  if (!statsData) {
    return (
      <div className="text-center py-8">
        <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
        <p className="text-muted-foreground">
          Email statistics will appear here once you start sending emails.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Email Statistics</h2>
          <p className="text-muted-foreground">
            Monitor your email delivery performance and engagement metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sent</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsData.totalSent.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 inline mr-1" />
              +12% from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Delivered</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsData.delivered.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {statsData.deliveryRate}% delivery rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsData.failed.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              <TrendingDown className="h-3 w-3 inline mr-1" />
              -5% from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Opened</CardTitle>
            <Activity className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsData.opened.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {statsData.openRate}% open rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time Series Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Email Activity Over Time</CardTitle>
            <CardDescription>
              Daily email sending and delivery trends
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={statsData.timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="sent"
                  stroke="#8884d8"
                  strokeWidth={2}
                  name="Sent"
                />
                <Line
                  type="monotone"
                  dataKey="delivered"
                  stroke="#82ca9d"
                  strokeWidth={2}
                  name="Delivered"
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke="#ff7300"
                  strokeWidth={2}
                  name="Failed"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Provider Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Provider Performance</CardTitle>
            <CardDescription>Delivery rates by email provider</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statsData.providerStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="provider" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="deliveryRate"
                  fill="#8884d8"
                  name="Delivery Rate (%)"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Provider Stats Table */}
      <Card>
        <CardHeader>
          <CardTitle>Provider Statistics</CardTitle>
          <CardDescription>
            Detailed breakdown by email service provider
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Provider</th>
                  <th className="text-right py-2">Sent</th>
                  <th className="text-right py-2">Delivered</th>
                  <th className="text-right py-2">Failed</th>
                  <th className="text-right py-2">Delivery Rate</th>
                </tr>
              </thead>
              <tbody>
                {statsData.providerStats.map((provider, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        {provider.provider}
                      </div>
                    </td>
                    <td className="text-right py-2">
                      {provider.sent.toLocaleString()}
                    </td>
                    <td className="text-right py-2">
                      {provider.delivered.toLocaleString()}
                    </td>
                    <td className="text-right py-2">
                      {provider.failed.toLocaleString()}
                    </td>
                    <td className="text-right py-2">
                      <Badge
                        variant={
                          provider.deliveryRate > 95 ? 'default' : 'secondary'
                        }
                      >
                        {provider.deliveryRate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailStats;
